import { useCallback, useMemo } from "react";
import {
  Filter,
  SEGMENT_LIST_FORMAT,
  SEGMENT_LIST_ID,
  SEGMENT_LIST_TYPE,
} from "src/segment";
import { ContextKey } from "src/utils/product-list-context-keys";
import { trackProductListFilteredEvent } from "src/tracking/events/product-list-filtered";
import { trackProductListFilterResetEvent } from "src/tracking/events/product-list-filter-reset";
import { listingStore } from "src/stores/listing/listing.store";

type UseFilterTrackingProps = {
  listFormat: SEGMENT_LIST_FORMAT;
  listId: SEGMENT_LIST_ID | string;
  listType: SEGMENT_LIST_TYPE | ContextKey;
  shopType?: string;
  resultId?: string;
};

type UseFilterTrackingResult = {
  trackFilters: (filtersApplied: Filter[]) => void;
  trackFilterReset: () => void;
};

function useFilterTracking({
  listFormat,
  listId,
  listType,
  shopType,
  resultId,
}: UseFilterTrackingProps): UseFilterTrackingResult {
  const meta = useMemo(
    () => ({ listFormat, listId, listType, shopType }),
    [listFormat, listId, listType, shopType]
  );

  const trackFilters = useCallback(
    (filtersApplied: Filter[]) => {
      if (filtersApplied.length === 0) {
        return;
      }

      const { appliedFacets } = listingStore.getSnapshot();

      void trackProductListFilteredEvent({
        resultId,
        meta,
        searchState: appliedFacets,
        filterAction: "filter",
        filtersApplied,
      });
    },
    [resultId, meta]
  );

  const trackFilterReset = useCallback(() => {
    void trackProductListFilterResetEvent({
      resultId,
      meta,
    });
  }, [resultId, meta]);

  return { trackFilters, trackFilterReset };
}

export { useFilterTracking };
